import { BehaviorSubject, fromEvent, merge, Observable } from 'rxjs';
import { share, skip, tap } from 'rxjs/operators';
import { createConnectingService } from '../base/connectingService';
import { ElementService } from './elementService';
import { createMemoryFocusService, FocusService } from './focusService';

export type FocusableElement = Element & HTMLOrSVGElement;

export function createElementFocusService(element$: Observable<FocusableElement | null>): FocusService {
  return createConnectingService(
    element$,
    createMemoryFocusService,
    element => new ElementFocusService(element),
    (nextService, prevService) => {
      nextService.setActive(prevService.isActive);
    },
  );
}

export class ElementFocusService extends ElementService<FocusableElement> implements FocusService {
  constructor(element: FocusableElement) {
    const isPendingFocus$ = new BehaviorSubject<boolean>(false);

    super(element, {
      changes$: merge(fromEvent(element, 'focus'), fromEvent(element, 'blur'), isPendingFocus$.pipe(skip(1))),
      effects$: fromEvent<KeyboardEvent>(element, 'keydown').pipe(
        tap(event => {
          if (!event.defaultPrevented && event.key === 'Escape' && this.isActive) {
            this.blur();

            event.preventDefault();
          }
        }),
        share(),
      ),
    });

    this.isPendingFocus$ = isPendingFocus$;
  }

  get isActive() {
    return this.element.contains(this.document.activeElement);
  }

  setActive(isActive: boolean) {
    if (isActive) {
      this.focus();
    } else {
      this.blur();
    }
  }

  private readonly isPendingFocus$: BehaviorSubject<boolean>;

  get isPendingFocus() {
    return this.isPendingFocus$.getValue();
  }

  focus() {
    // Avoid frivolous focus changes, in case they trigger events.
    if (this.isActive) {
      return;
    }

    // Allow subscribers to do stuff before actively perfoming a focus, such as restoring the selection.
    this.isPendingFocus$.next(true);
    this.isPendingFocus$.next(false);

    this.element.focus();
  }

  blur() {
    this.element.blur();
  }
}
